import type maplibregl from "maplibre-gl";

export type BasemapTheme = "light" | "dark" | "dark_matter";
// Matches BaseMapId (map/presets.ts) exactly so the App-level `baseMap` state
// passes straight through with no translation layer to keep in sync.
export type BasemapGround = "streets" | "aerial" | "topo" | "night" | "true-color";

const LONGDO_TRAFFIC_TILES = "https://msv.longdo.com/maps/traffic/{z}/{x}/{y}.pbf";
const ESRI_IMAGERY_TILES =
  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const OPENTOPO_TILES = [
  "https://a.tile.opentopomap.org/{z}/{x}/{y}.png",
  "https://b.tile.opentopomap.org/{z}/{x}/{y}.png",
  "https://c.tile.opentopomap.org/{z}/{x}/{y}.png",
];
// GIBS near-real-time products lag behind "today" by a day or more, so
// requesting yesterday's date can 404 before that day's mosaic is published.
// Not the whole story for Night, though — see the product-id note below.
function gibsDate(daysAgo = 3): string {
  return new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
function gibsTiles(productId: string, level: number, format: "png" | "jpg"): string {
  return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${productId}/default/${gibsDate()}` +
    `/GoogleMapsCompatible_Level${level}/{z}/{y}/{x}.${format}`;
}

/**
 * Ground + label rasters: Esri's Canvas Gray series, keyless on the same
 * arcgisonline host the Aerial ground already uses (CSP-covered).
 *
 * These replaced CARTO's free raster endpoints on 2026-08-28: CARTO
 * retired anonymous access and now bakes a diagonal "API KEY REQUIRED ·
 * carto.com/basemaps/apikey" watermark into every tile — still HTTP 200,
 * so nothing errored; the maps just quietly grew graffiti. Keyless-by-
 * design beats a browser-exposed key (standing project rule), and the
 * quiet gray canvas matches the AXIOM paper look better than CARTO did.
 *
 * The service's own metadata advertises LODs through z23, but that's the
 * cache's ceiling everywhere, not Bangkok's: past z16 it has no tiles for
 * this region and serves a 2.5 KB placeholder baked with "Map data not
 * yet available" instead — same failure shape as the CARTO watermark
 * (HTTP 200, silent). Verified by fetching real Chula-area tile
 * coordinates: z13-16 return real ~15-19 KB map tiles, z17+ return the
 * placeholder. maxzoom must stay at CANVAS_MAXZOOM so MapLibre overzooms
 * the z16 tile at closer zooms instead of requesting the placeholder.
 */
const CANVAS_MAXZOOM = 16;
function esriCanvasTiles(theme: BasemapTheme, kind: "base" | "reference"): string[] {
  const shade = theme === "light" ? "Light" : "Dark";
  const service = kind === "base" ? `World_${shade}_Gray_Base` : `World_${shade}_Gray_Reference`;
  return [
    `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${service}/MapServer/tile/{z}/{y}/{x}`,
  ];
}

// ── BKKx 3D building fabric (ported from atlas.nonarkara.org) ──────────────
//
// Native MapLibre vector-tile extrusions keep the city fabric below traffic
// and labels. Use the existing OpenFreeMap building source at every 3D zoom:
// the Pages-hosted archive does not support the byte ranges PMTiles requires.
const OPENFREEMAP_TILEJSON = "https://tiles.openfreemap.org/planet";

type Expr = maplibregl.ExpressionSpecification;

// Height with sanity clamp — OSM occasionally carries corrupt levels tags
// that would extrude 50-petametre sky-pillars. Out-of-range → 9 m default.
const HEIGHT_GET: Expr = [
  "case",
  ["all",
    [">=", ["coalesce", ["get", "render_height"], ["get", "height"], 0], 1],
    ["<=", ["coalesce", ["get", "render_height"], ["get", "height"], 0], 500],
  ],
  ["coalesce", ["get", "render_height"], ["get", "height"], 9],
  9,
];
// Base must stay strictly below height — base >= height makes MapLibre emit
// degenerate roof triangles that read as sawteeth.
const MIN_HEIGHT_RAW: Expr = [
  "coalesce",
  ["get", "render_min_height"],
  ["get", "min_height"],
  ["get", "base_height"],
  0,
];
const MIN_HEIGHT_GET: Expr = [
  "case",
  [">=", MIN_HEIGHT_RAW, HEIGHT_GET],
  0,
  ["max", 0, MIN_HEIGHT_RAW],
];
// Skip collapsed/non-3D footprints that would fang the skyline.
const EXTRUSION_FILTER: maplibregl.FilterSpecification = [
  "all",
  ["!=", ["get", "hide_3d"], true],
  [">", HEIGHT_GET, 0],
] as unknown as maplibregl.FilterSpecification;
// Opacity reaches 1.0 by street zoom — translucent extrusions z-fight into
// glittery roof shards on Chrome/macOS (atlas finding, kept verbatim).
const EXTRUSION_OPACITY: Expr = [
  "interpolate", ["linear"], ["zoom"],
  11, 0.5,
  13, 0.82,
  14.5, 1,
  17, 1,
];

/**
 * Data-coded color by OSM `building` type — THE attribute surface. Swap or
 * extend this one expression to recolor the whole city by any property.
 * Palette is the atlas's type scheme retuned to AXIOM: gold for the sacred,
 * muted civic/commercial hues, and a theme-aware neutral for the untagged
 * mass so the campus (deck.gl, pink) stays the visual protagonist.
 */
function buildingColorByType(theme: BasemapTheme): Expr {
  const mass = theme === "light" ? "#cfc9be" : "#4b5563";
  return [
    "match", ["get", "building"],
    // sacred — gold family
    "temple", "#e0aa12", "pagoda", "#e0aa12", "shrine", "#d49c16",
    "wayside_shrine", "#d49c16", "monastery", "#c9941c", "chapel", "#c9941c",
    "church", "#c9941c", "mosque", "#c9941c",
    // civic / education / health
    "university", "#b7307a", "college", "#b7307a", "school", "#c05c92",
    "kindergarten", "#c05c92", "hospital", "#d95f5f", "government", "#8a7ba8",
    "public", "#8a7ba8", "civic", "#8a7ba8",
    // commercial / hospitality
    "commercial", "#7f9bb3", "office", "#7f9bb3", "retail", "#96a88f",
    "hotel", "#a58ec4", "supermarket", "#96a88f",
    // transport / industrial
    "train_station", "#6b8f71", "industrial", "#9a8f80", "warehouse", "#9a8f80",
    // residential mass
    "apartments", mass, "residential", mass, "house", mass, "terrace", mass,
    "detached", mass, "dormitory", "#b98bb0",
    mass,
  ] as unknown as Expr;
}

/**
 * Street-first MapLibre style used beneath deck.gl overlays.
 *
 * Longdo/iTIC traffic is consumed as the provider's live vector TileJSON,
 * never scraped or persisted. The two directional layers reproduce the
 * provider's green/amber/red road speeds while labels remain readable above.
 */
export function makeBasemap(
  theme: BasemapTheme,
  showLiveTraffic: boolean,
  ground: BasemapGround = "streets",
  /** 3D view modes: render the BKKx city-wide building fabric natively. */
  extrude3d = false,
): maplibregl.StyleSpecification {
  const sources: maplibregl.StyleSpecification["sources"] = {
    // Source ids keep their historic "carto-*" names so every existing
    // layer reference (basemap / labels-top, tests, beforeId anchors)
    // stays valid — only the tile provider behind them changed.
    "carto-base": {
      type: "raster",
      tiles: esriCanvasTiles(theme, "base"),
      tileSize: 256,
      attribution: "Esri, HERE, Garmin · © OpenStreetMap contributors",
      maxzoom: CANVAS_MAXZOOM,
    },
    "carto-labels": {
      type: "raster",
      tiles: esriCanvasTiles(theme, "reference"),
      tileSize: 256,
      maxzoom: CANVAS_MAXZOOM,
    },
  };

  // Every non-carto ground lives INSIDE the maplibre style (not a deck.gl
  // overlay) so live traffic + place labels keep rendering above it, and so
  // it's simply visible whenever selected — no zoom-gated visibility flags
  // to silently hide it. Topo/Night/True-color used to be deck.gl TileLayers
  // gated to "only visible below zoom 10-14"; at the app's normal campus
  // zoom (~14-17) that gate made them invisible no matter what the picker
  // said, which read as "toggling does nothing."
  if (ground === "aerial") {
    sources["esri-imagery"] = {
      type: "raster",
      tiles: [ESRI_IMAGERY_TILES],
      tileSize: 256,
      attribution: "Esri, Maxar, Earthstar Geographics",
      maxzoom: 19,
    };
  } else if (ground === "topo") {
    sources["opentopo"] = {
      type: "raster",
      tiles: OPENTOPO_TILES,
      tileSize: 256,
      attribution: "© OpenTopoMap (CC-BY-SA)",
      maxzoom: 17,
    };
  } else if (ground === "night") {
    // "VIIRS_SNPP_DayNightBand_ENCC" — the identifier this used originally —
    // doesn't exist in GIBS's current capabilities (confirmed via
    // GetCapabilities; every tile request 404'd regardless of date). The
    // live product is VIIRS_NOAA20_DayNightBand_At_Sensor_Radiance.
    sources["gibs-night"] = {
      type: "raster",
      tiles: [gibsTiles("VIIRS_NOAA20_DayNightBand_At_Sensor_Radiance", 8, "png")],
      tileSize: 256,
      attribution: "NASA GIBS · VIIRS Day/Night Band",
      maxzoom: 8,
    };
  } else if (ground === "true-color") {
    sources["gibs-true-color"] = {
      type: "raster",
      tiles: [gibsTiles("VIIRS_NOAA20_CorrectedReflectance_TrueColor", 9, "jpg")],
      tileSize: 256,
      attribution: "NASA GIBS · VIIRS true color",
      maxzoom: 9,
    };
  }

  const GROUND_SOURCE: Record<BasemapGround, string> = {
    streets: "carto-base",
    aerial: "esri-imagery",
    topo: "opentopo",
    night: "gibs-night",
    "true-color": "gibs-true-color",
  };
  const layers: maplibregl.LayerSpecification[] = [
    { id: "basemap", type: "raster", source: GROUND_SOURCE[ground] },
  ];

  // BKKx 3D building fabric — beneath traffic + labels so live road speed
  // and place names always read above the massing.
  if (extrude3d) {
    sources["openfreemap"] = {
      type: "vector",
      url: OPENFREEMAP_TILEJSON,
      attribution: "© OpenFreeMap · © OpenMapTiles",
    };
    const paint: maplibregl.FillExtrusionLayerSpecification["paint"] = {
      "fill-extrusion-color": buildingColorByType(theme),
      "fill-extrusion-height": HEIGHT_GET,
      "fill-extrusion-base": MIN_HEIGHT_GET,
      "fill-extrusion-opacity": EXTRUSION_OPACITY,
      "fill-extrusion-vertical-gradient": true,
    };
    layers.push(
      {
        id: "bkkx-buildings-3d",
        type: "fill-extrusion",
        source: "openfreemap",
        "source-layer": "building",
        minzoom: 11,
        maxzoom: 15,
        filter: EXTRUSION_FILTER,
        paint,
      },
      {
        id: "bkkx-buildings-3d-hi",
        type: "fill-extrusion",
        source: "openfreemap",
        "source-layer": "building",
        minzoom: 15,
        filter: EXTRUSION_FILTER,
        paint,
      },
    );
  }

  if (showLiveTraffic) {
    sources["longdo-traffic"] = {
      type: "vector",
      tiles: [LONGDO_TRAFFIC_TILES],
      minzoom: 5,
      maxzoom: 12,
      bounds: [97.37, 5.69, 105.59, 20.42],
      attribution: "Longdo Traffic / iTIC",
    };
    const width: maplibregl.ExpressionSpecification = [
      "interpolate", ["linear"], ["zoom"], 5, 1.2, 12, 3, 17, 5,
    ];
    const offset: maplibregl.ExpressionSpecification = [
      "interpolate", ["linear"], ["zoom"], 5, 1, 12, 2, 17, 3,
    ];
    const reverseOffset: maplibregl.ExpressionSpecification = [
      "interpolate", ["linear"], ["zoom"], 5, -1, 12, -2, 17, -3,
    ];
    layers.push(
      {
        id: "traffic-forward",
        type: "line",
        source: "longdo-traffic",
        "source-layer": "traffic",
        minzoom: 5,
        filter: ["!=", ["get", "fillcolor"], ""],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-opacity": 0.9,
          "line-color": ["concat", "#", ["get", "fillcolor"]],
          "line-width": width,
          "line-offset": reverseOffset,
        },
      },
      {
        id: "traffic-reverse",
        type: "line",
        source: "longdo-traffic",
        "source-layer": "traffic",
        minzoom: 5,
        filter: ["!=", ["get", "fillcolor_r"], ""],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-opacity": 0.9,
          "line-color": ["concat", "#", ["get", "fillcolor_r"]],
          "line-width": width,
          "line-offset": offset,
        },
      },
    );
  }

  layers.push({
    id: "labels-top",
    type: "raster",
    source: "carto-labels",
    paint: { "raster-opacity": theme === "light" ? 0.9 : 0.85 },
  });

  return { version: 8, sources, layers };
}
